/**
 * The prose block still being written: the same blocks `renderAnswer` draws for a settled answer,
 * over text held until each line is complete and then played back a whole word per beat
 * (`useTextPlayback`), with each word fading in once through three tints (DECK-1692, after Kobra's
 * streaming text). While a line is held nothing of it is drawn, and `onHoldingChange` tells the thread,
 * so it can show that the agent is still thinking.
 *
 * A component only because a hook cannot run inside `DeckThread`'s block loop. It returns a fragment,
 * not a wrapper, so the assistant slot's gap still falls between the paragraphs themselves.
 *
 * Three rules keep it honest:
 * - **Settled text is untouched.** Every block once it settles, all of history, reduced motion and any
 *   render without frames draw exactly what `renderAnswer` draws (a test holds the markups equal).
 *   Only the words of the block still being written get spans.
 * - **Nothing happens when a fade ends.** A word's span is keyed by its place among the block's words
 *   and keeps its key for as long as the block is live; the fade is a CSS animation with no fill and
 *   no end handler, so its last frame is simply the span at rest.
 * - **Marks never flash raw.** The tail of a streaming answer is drawn as what it will become: an open
 *   `**` or backtick is closed for drawing, a link still being written draws as its label, and a list
 *   marker or table row waits for the words that make it one. Nothing changes kind under a word.
 */
import { Fragment, memo, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import {
  AnswerHeading,
  AssistantProse,
  DataTable,
  OrderedList,
  type ChatTheme,
} from '~/design-system/components/v1_5/Chat';
import { useTextPlayback } from '../activity/useSmoothText';
import styles from './LiveProse.module.css';
import { parseAnswer, tableColumns, type InlineRun, type ProseNode } from './markdown';
import rich from './RichProse.module.css';

export interface LiveProseProps {
  text: string;
  keyPrefix: string;
  theme: ChatTheme;
  /**
   * The stream has closed this block: `textBoundary` or `done` arrived, or a later block exists. A
   * line that never ends a sentence is held until this is true, then all of it plays.
   */
  sealed?: boolean;
  /**
   * Called whenever the block starts or stops holding: text has arrived that is not shown because its
   * line is not complete. While it holds, nothing of that line is drawn, so the thread shows that the
   * agent is still thinking.
   */
  onHoldingChange?: (holding: boolean) => void;
}

/** A static render runs no effects and has no frames: nothing is arriving, so nothing fades. */
const canAnimate = () => typeof requestAnimationFrame === 'function';

export function LiveProse({ text, keyPrefix, theme, sealed = false, onHoldingChange }: LiveProseProps) {
  const { shown, holding } = useTextPlayback(text, true, sealed);
  const clock = useRef<WordClock | null>(null);
  clock.current ??= new WordClock();
  const reportHolding = useRef(onHoldingChange);
  reportHolding.current = onHoldingChange;
  useEffect(() => {
    if (!holding) return;
    reportHolding.current?.(true);
    // Stopping, or unmounting while held (the turn settled), both end the hold for whoever listens.
    return () => reportHolding.current?.(false);
  }, [holding]);
  // Under reduced motion the words still arrive whole and on the beat; they just appear, with no span.
  const still = usePrefersReducedMotion();
  return <>{renderLiveAnswer(shown, keyPrefix, theme, !still && canAnimate() ? clock.current : null)}</>;
}

/* ── when each word appeared ─────────────────────────────────────────────────────────────────── */

/**
 * When each word of one live block first appeared, by its place among the block's words in reading
 * order. The text only grows, a whole word at a time, so a word's place never changes under it.
 */
export class WordClock {
  private readonly bornAt: number[] = [];
  private source = '';

  /** A render of `source` is starting. Text that was replaced rather than extended starts over. */
  begin(source: string): void {
    if (!source.startsWith(this.source)) this.bornAt.length = 0;
    this.source = source;
  }

  /** When word `place` first appeared: now, the first time it is drawn. */
  birth(place: number, nowMs: number): number {
    return (this.bornAt[place] ??= nowMs);
  }
}

/** Splits run text into words, wrapping each word the clock says is new. */
class WordWriter {
  count = 0;

  constructor(
    private readonly clock: WordClock | null,
    private readonly nowMs: number,
  ) {}

  /** `text` with each word in its own span; exactly as `renderAnswer` passes it when there is no clock. */
  write(text: string): ReactNode {
    if (!this.clock) return text;
    const nodes: ReactNode[] = [];
    let plain = '';
    for (const part of text.split(/(\s+)/)) {
      if (!part) continue;
      if (/^\s/.test(part)) {
        plain += part;
        continue;
      }
      const place = this.count;
      this.count += 1;
      if (plain) nodes.push(plain);
      plain = '';
      nodes.push(<Word key={`w${place}`} text={part} bornAt={this.clock.birth(place, this.nowMs)} nowMs={this.nowMs} />);
    }
    if (nodes.length === 0) return text;
    if (plain) nodes.push(plain);
    return nodes;
  }

  /** Words drawn by a component that only takes strings (a heading, a table): counted, never wrapped. */
  skip(text: string): void {
    if (this.clock) this.count += text.split(/\s+/).filter(Boolean).length;
  }
}

interface WordProps {
  text: string;
  bornAt: number;
  nowMs: number;
}

const Word = memo(
  function Word({ text, bornAt, nowMs }: WordProps) {
    // Read once, when the span mounts. A word mounted again mid-fade (the run around it changed
    // element as the text grew, a label becoming a link) picks the fade up where it was; one mounted
    // after its fade shows at rest. A re-render never touches the span.
    const [offsetMs] = useState(() => Math.min(0, Math.round(bornAt - nowMs)));
    return (
      <span
        className={styles.word}
        data-live-word=""
        style={offsetMs < 0 ? { animationDelay: `${offsetMs}ms` } : undefined}
      >
        {text}
      </span>
    );
  },
  (before, after) => before.text === after.text && before.bornAt === after.bornAt,
);

/* ── the blocks ──────────────────────────────────────────────────────────────────────────────── */

/**
 * `renderAnswer` for a block that is still being written: the same components, classes and keys,
 * with every word in a span that fades in once, the first time it is drawn. `clock` null draws no
 * spans at all: reduced motion, and any render that has no frames.
 */
export function renderLiveAnswer(
  text: string,
  keyPrefix: string,
  theme: ChatTheme,
  clock: WordClock | null,
  nowMs: number = performance.now(),
): ReactNode[] {
  clock?.begin(text);
  const words = new WordWriter(clock, nowMs);
  return parseAnswer(drawableText(text)).map((node, at) => liveNode(node, `${keyPrefix}-${at}`, theme, words));
}

/** `RichProse`'s `Inline`, word by word. */
function liveInline(runs: InlineRun[], keyPrefix: string, words: WordWriter): ReactNode[] {
  return runs.map((run, at) => {
    const key = `${keyPrefix}-${at}`;
    const text = words.write(run.text);
    switch (run.kind) {
      case 'bold':
        return (
          <strong key={key} className={rich.bold}>
            {text}
          </strong>
        );
      case 'italic':
        return (
          <em key={key} className={rich.italic}>
            {text}
          </em>
        );
      case 'code':
        return (
          <code key={key} className={rich.code}>
            {text}
          </code>
        );
      case 'link':
        return (
          <a key={key} className={rich.link} href={run.href} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        );
      default:
        return <span key={key}>{text}</span>;
    }
  });
}

/** `RichProse`'s `nodeToElement`, word by word. Called in reading order, which numbers the words. */
function liveNode(node: ProseNode, key: string, theme: ChatTheme, words: WordWriter): ReactNode {
  switch (node.kind) {
    case 'heading':
      words.skip(node.text);
      return (
        <AnswerHeading key={key} theme={theme}>
          {node.text}
        </AnswerHeading>
      );

    case 'steps':
      return (
        <OrderedList
          key={key}
          theme={theme}
          items={node.items.map((runs, at) => (
            <Fragment key={at}>{liveInline(runs, `${key}-${at}`, words)}</Fragment>
          ))}
        />
      );

    case 'table':
      for (const cell of [...node.columns, ...node.rows.flat()]) words.skip(cell);
      return (
        <DataTable
          key={key}
          theme={theme}
          columns={tableColumns(node.columns, node.rows)}
          rows={node.rows}
          label={node.columns.filter(Boolean).join(', ') || undefined}
        />
      );

    case 'bullets':
      return (
        <AssistantProse key={key} theme={theme}>
          <div className={rich.bullets}>
            {node.items.map((runs, at) => (
              <div key={at} className={rich.bullet}>
                <span className={rich.marker} aria-hidden="true">
                  •
                </span>
                <span className={rich.bulletBody}>{liveInline(runs, `${key}-${at}`, words)}</span>
              </div>
            ))}
          </div>
        </AssistantProse>
      );

    case 'code':
      return (
        <AssistantProse key={key} theme={theme}>
          <code className={rich.codeBlock}>{words.write(node.text)}</code>
        </AssistantProse>
      );

    default:
      return (
        <AssistantProse key={key} theme={theme}>
          {liveInline(node.runs, key, words)}
        </AssistantProse>
      );
  }
}

/* ── the tail of a streaming answer ──────────────────────────────────────────────────────────── */

const FENCE = /^\s*```/;
/** A list, heading or quote marker alone on the last line: the block it opens has no words yet. */
const BARE_MARKER = /^\s*(?:[-*+]|\d{1,3}[.)]|#{1,6}|>)\s*$/;

const isSpace = (char: string | undefined) => char !== undefined && /\s/.test(char);
const isTableRow = (line: string) => line.trim().startsWith('|') && line.trim().includes('|', 1);
const isTableDelimiter = (line: string) => {
  const trimmed = line.trim();
  if (!trimmed.startsWith('|')) return false;
  return trimmed
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .every((cell) => /^:?-+:?$/.test(cell.trim()));
};

/**
 * The last line starts with a pipe but is not drawable as part of a table yet: a header whose
 * delimiter has not arrived, a delimiter still being written, or a body row with no cell break.
 * Drawn now, each would be a paragraph that turns into a table a moment later.
 */
function unfinishedTableLine(lines: string[], at: number): boolean {
  if (!lines[at].trim().startsWith('|')) return false;
  let start = at;
  while (start > 0 && lines[start - 1].trim().startsWith('|')) start -= 1;
  if (at === start) return true;
  if (at === start + 1) return !isTableDelimiter(lines[at]) && /^[\s|:-]*$/.test(lines[at]);
  return isTableRow(lines[start]) && isTableDelimiter(lines[start + 1]) && !isTableRow(lines[at]);
}

/**
 * What of a streaming answer can be drawn without anything changing kind a moment later. Trailing
 * lines that only open a block wait for their first words; marks opened on the last line are closed
 * for drawing. A finished, well-formed answer comes back unchanged.
 */
export function drawableText(text: string): string {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const insideFence = (at: number) => lines.slice(0, at).filter((line) => FENCE.test(line)).length % 2 === 1;
  while (lines.length > 0) {
    const at = lines.length - 1;
    if (insideFence(at) || !lines[at].trim()) break;
    if (!BARE_MARKER.test(lines[at]) && !unfinishedTableLine(lines, at)) break;
    lines.pop();
  }
  const at = lines.length - 1;
  if (at >= 0 && !insideFence(at) && !FENCE.test(lines[at]) && !lines[at].trim().startsWith('|')) {
    lines[at] = closeMarks(lines[at]);
  }
  return lines.join('\n');
}

/**
 * Marks opened on `line` and not closed yet, closed for drawing. An open `**`, `*` or backtick gets
 * its closer, so the words already inside draw bold, italic or as code from their first frame; a link
 * still being written draws as its label alone; a mark with nothing inside it yet is left out. When
 * the stream's own closer arrives it replaces the drawn one and no word moves.
 */
export function closeMarks(line: string): string {
  let code = -1;
  let label = -1;
  let href = -1;
  let bold = -1;
  let italic = -1;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (code >= 0) {
      if (char === '`') code = -1;
      continue;
    }
    if (href >= 0) {
      if (char === ')') href = label = -1;
      continue;
    }
    if (char === '`') {
      code = i;
    } else if (char === '[') {
      label = i;
    } else if (char === ']' && label >= 0) {
      if (line[i + 1] === '(') {
        href = i;
        i += 1;
      } else {
        label = -1;
      }
    } else if (char === '*' && line[i + 1] === '*') {
      bold = bold >= 0 ? -1 : i;
      i += 1;
    } else if (char === '*') {
      // The parser's own rule: an italic opens against a word's start and closes against its end.
      if (italic >= 0) {
        if (!isSpace(line[i - 1])) italic = -1;
      } else if ((i === 0 || isSpace(line[i - 1])) && line[i + 1] !== undefined && !isSpace(line[i + 1])) {
        italic = i;
      }
    }
  }

  const closeOrDrop = (at: number, mark: string) => (drawn: string) =>
    drawn.slice(at + mark.length).trim() ? drawn + mark : drawn.slice(0, at) + drawn.slice(at + mark.length);
  const fixes: { at: number; apply: (drawn: string) => string }[] = [];
  if (code >= 0) fixes.push({ at: code, apply: closeOrDrop(code, '`') });
  if (bold >= 0) fixes.push({ at: bold, apply: closeOrDrop(bold, '**') });
  if (italic >= 0) fixes.push({ at: italic, apply: closeOrDrop(italic, '*') });
  // A link still being written: its half-typed address goes, then its opening bracket.
  if (href >= 0) fixes.push({ at: href, apply: (drawn) => drawn.slice(0, href) });
  if (label >= 0) fixes.push({ at: label, apply: (drawn) => drawn.slice(0, label) + drawn.slice(label + 1) });
  // Latest mark first: each fix touches only its own position or the end of the line, so the
  // positions of the marks still to fix, all earlier in the line, stay where they were.
  return fixes.sort((a, b) => b.at - a.at).reduce((drawn, fix) => fix.apply(drawn), line);
}

/* ── reduced motion ──────────────────────────────────────────────────────────────────────────── */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const prefersReducedMotion = () => typeof matchMedia === 'function' && matchMedia(REDUCED_MOTION).matches;
const onMotionPreference = (onChange: () => void) => {
  if (typeof matchMedia !== 'function') return () => {};
  const query = matchMedia(REDUCED_MOTION);
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
};

/** The OS reduced-motion setting, right from the first render and live when it changes. */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(onMotionPreference, prefersReducedMotion, prefersReducedMotion);
}
